import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { Typography } from 'shared/ui/Typography'
import { Icon } from 'shared/ui/Icon'
import { Button, IconButton, Scrollbar, SpinnerLoader } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { LINK_AI_CHECKR_LEARN_MORE } from 'shared/constants/links'
import type { AISidebarStore } from 'pages/settings/pages/compliance/store/aiSidebarStore'
import { siggestionRedirect, statuses } from './constants'
import styles from './styles.module.scss'

type IAISidebarProps = {
  sidebarStore?: AISidebarStore
}

export const AISidebar = observer(({ sidebarStore }: IAISidebarProps) => {
  const {
    setIsVisible,
    approvalChance = 'medium',
    isMobileView,
    isLoading,
    setIsShowHeaderStatus,
    siggestionsByStep,
  } = sidebarStore || {}

  return (
    <div className={classNames(styles.wrapper, styles[approvalChance])}>
      {!isMobileView && (
        <div className={styles.closeButtonWrapper}>
          <IconButton
            className={styles.close}
            ariaLabel='close_setting'
            icon='close'
            variant='icon'
            color='tertiary'
            size='medium'
            onClick={() => {
              setIsVisible?.(false)
              setIsShowHeaderStatus?.(true)
            }}
          />
        </div>
      )}
      <div className={styles.titleWrap}>
        <div className={styles.titleWrap__title}>
          <Icon icon={'starsGradient'} />
          <Typography ariaLabel='aiChecker_title' variant='body-sm-regular'>
            AI Checkr
          </Typography>
        </div>
      </div>
      <div className={styles.approvalChanceWrap}>
        {isLoading ? (
          <SpinnerLoader ariaLabel={'AISidebar'} />
        ) : (
          <Icon icon={statuses[approvalChance].icon} fontSize={32} className={styles.icon} />
        )}
        <Typography
          ariaLabel='approvalChance_title'
          variant='heading-md'
          marginTop={'12px'}
          marginBottom={'24px'}
        >
          {isLoading ? 'Checking for issues ...' : statuses[approvalChance].title}
        </Typography>
      </div>

      {!isLoading && (
        <Scrollbar scrollToBottomTrigger viewClassName={styles.descriptionWrap}>
          <div className={styles.descriptionWrap__description}>
            <Typography ariaLabel='approvalChance_description' variant='body-rg-regular'>
              {statuses[approvalChance].description}
            </Typography>
          </div>
          {Object.entries(siggestionsByStep || {}).map(([stepKey, items]) => (
            <div className={styles.stepWrap} key={stepKey}>
              {stepKey !== '0' && (
                <Typography ariaLabel='step' variant='body-md-regular'>
                  Step {stepKey}
                </Typography>
              )}
              {items.map((item, index) => (
                <div className={styles.stepCard} key={index}>
                  <div
                    className={classNames(
                      styles.stepCard__content,
                      styles[item.severity.toLowerCase()]
                    )}
                  >
                    <Typography ariaLabel='step_title' variant='body-lg-medium'>
                      {item.title}
                    </Typography>
                    <Typography
                      ariaLabel='step_description'
                      variant='body-rg-regular'
                      color='var(--content-primary-secondary)'
                      className={styles.stepDescription}
                    >
                      {item.text}
                    </Typography>
                  </div>
                  {siggestionRedirect[item.step] && (
                    <Button
                      text='Update'
                      typeBtn='contained'
                      contained='tertiary'
                      onClick={() => {
                        uiStore.changeRoute({
                          path: siggestionRedirect[item.step],
                          options: {
                            state: { focusField: item.field },
                          },
                        })
                      }}
                      className={styles.stepButton}
                    />
                  )}
                  {item.field === 'brand.website' && (
                    <Button
                      tag='a'
                      typeBtn='contained'
                      contained='tertiary'
                      text={'Learn more'}
                      href={LINK_AI_CHECKR_LEARN_MORE}
                      target='_blank'
                      rel='noreferrer'
                      style={{ textDecoration: 'none' }}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
        </Scrollbar>
      )}
    </div>
  )
})
